import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import type { ProcessingStage } from '../../types/processing';
import { cn } from '@/lib/utils';
import Vib3Mark from '../landing/Vib3Mark';
import '../onboarding/onboarding.css';

/**
 * The analysis stages as one vertical timeline.
 *
 * Replaces the previous list of independent cards. The point of the change is
 * that a pipeline reads as a pipeline: a dashed rail runs the whole way down,
 * fills in orange up to where the run has actually got to, and the running
 * stage is the only one that expands to explain itself. Everything else stays
 * a single scannable line.
 *
 * The rail's filled height is a real measurement (`--line-h`), taken from the
 * last completed badge's centre — computing it from an index would drift as
 * soon as one stage wraps to two lines.
 */

/** Elapsed counter for the running stage, as ``M:SS``. Blank for the first
 *  second so a freshly-started stage doesn't flash ``0:00``. */
function useElapsedLabel(startedAt: number | undefined, active: boolean): string {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!active || !startedAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active, startedAt]);
  if (!active || !startedAt) return '';
  const seconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  if (seconds < 1) return '';
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

export default function StageTimeline({ stages }: { stages: ProcessingStage[] }) {
  const listRef = useRef<HTMLDivElement>(null);
  const lastDoneIndex = stages.reduce(
    (acc, stage, i) => (stage.status === 'completed' ? i : acc),
    -1,
  );

  // Measure after layout, before paint, so the rail never renders at the wrong
  // length for a frame.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    if (lastDoneIndex < 0) {
      list.style.setProperty('--line-h', '0px');
      return;
    }
    const row = list.children[lastDoneIndex] as HTMLElement | undefined;
    const badge = row?.querySelector<HTMLElement>('[data-badge]');
    if (!row || !badge) return;
    const centre = row.offsetTop + badge.offsetTop + badge.offsetHeight / 2;
    list.style.setProperty('--line-h', `${Math.max(0, centre - 24)}px`);
  }, [lastDoneIndex, stages.length]);

  return (
    <div ref={listRef} className="ob-timeline flex flex-col gap-0.5 pb-2">
      {stages.map((stage, i) => (
        <StageRow key={stage.id} stage={stage} index={i} />
      ))}
    </div>
  );
}

function StageRow({ stage, index }: { stage: ProcessingStage; index: number }) {
  const active = stage.status === 'active';
  const done = stage.status === 'completed';
  const elapsed = useElapsedLabel(stage.startedAt, active);
  const stepNum = stage.subtaskCount ?? 1;

  return (
    <div
      className={cn(
        'ob-step relative grid grid-cols-[28px_1fr_auto] items-start gap-3.5 rounded-xl px-1.5 py-2.5 transition-colors',
        active && 'ob-step--active bg-gradient-to-b from-secondary/[0.04] to-transparent',
      )}
    >
      <div
        data-badge
        className={cn(
          'ob-step__badge relative z-10 flex size-7 items-center justify-center rounded-full border-[1.5px] bg-card transition-colors',
          active && 'border-transparent',
          done && 'border-secondary bg-secondary text-secondary-foreground',
          !active && !done && 'text-muted-foreground',
        )}
      >
        {active ? (
          <Vib3MarkSpinner />
        ) : done ? (
          <Check className="size-3" strokeWidth={2.8} />
        ) : (
          <span className="font-mono text-[11px] font-semibold leading-none">{index + 1}</span>
        )}
      </div>

      <div className="min-w-0">
        <div
          className={cn(
            'text-[14.5px] font-semibold leading-7 tracking-[-0.005em] transition-colors',
            active ? 'text-foreground' : done ? 'text-foreground/70' : 'text-muted-foreground',
          )}
        >
          {stage.label}
        </div>
        {stage.subtaskLabel && (
          <div className="ob-step__desc text-[12.5px] leading-snug text-muted-foreground">
            {stage.subtaskTotal ? `Step ${stepNum} of ${stage.subtaskTotal}` : `Step ${stepNum}`} ·{' '}
            {stage.subtaskLabel}
          </div>
        )}
      </div>

      <div
        className={cn(
          'inline-flex items-center gap-1.5 font-mono text-[11px] leading-7 tabular-nums',
          active ? 'text-secondary' : done ? 'text-muted-foreground' : 'text-muted-foreground/60',
        )}
      >
        {done && <Check className="size-3 text-success" strokeWidth={3} />}
        {active
          ? (elapsed || `${Math.round(Math.max(0, Math.min(100, stage.progress)))}%`)
          : done
            ? 'Done'
            : '—'}
      </div>
    </div>
  );
}

function Vib3MarkSpinner() {
  return (
    <span className="ob-step__mark block size-[22px]" aria-hidden>
      <Vib3Mark />
    </span>
  );
}
